import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  BarChart, Bar,
} from "recharts";
import {
  ShieldCheck, RefreshCw, ArrowLeft, ExternalLink, ChevronRight, Check, X,
  Activity, LineChart as LineIcon, ShieldAlert, HeartPulse, Info, Server, FileLock2, Cog, Gauge, ClipboardList, Camera,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { useToast } from "../hooks/use-toast";
import { useAuth } from "../context/AuthContext";
import { cn } from "../lib/utils";
import { fmtMDT } from "../lib/time";

const CRIT_META = {
  critical: { label: "Critical Risk", color: "#7f1d1d", ring: "border-red-800 bg-red-800 text-white" },
  high:     { label: "High Risk",     color: "#ef4444", ring: "border-red-400 bg-red-400 text-white" },
  moderate: { label: "Moderate Risk", color: "#f59e0b", ring: "border-amber-400 bg-amber-400 text-white" },
  low:      { label: "Low Risk",      color: "#3b82f6", ring: "border-blue-400 bg-blue-400 text-white" },
};

// Semicircle gauge for the Environment Security Rating. 0 → WEAK (left), 100 → STRONG (right).
function GaugeArc({ score = 0 }) {
  const clamped = Math.max(0, Math.min(100, score));
  const angle = (clamped / 100) * 180; // 0 → left, 180 → right
  const size = 260;
  const cx = size / 2;
  const cy = size / 2 + 10;
  const r = 100;
  const stroke = 24;
  // Build the arc as 4 colored segments (red → orange → yellow → green)
  const segments = [
    { from: 0,   to: 45,  color: "#ef4444" },
    { from: 45,  to: 90,  color: "#f97316" },
    { from: 90,  to: 135, color: "#facc15" },
    { from: 135, to: 180, color: "#22c55e" },
  ];
  const polar = (deg) => {
    const rad = (Math.PI * (180 - deg)) / 180;
    return { x: cx + r * Math.cos(rad), y: cy - r * Math.sin(rad) };
  };
  const arcPath = (from, to) => {
    const p0 = polar(from);
    const p1 = polar(to);
    const large = to - from > 180 ? 1 : 0;
    return `M ${p0.x} ${p0.y} A ${r} ${r} 0 ${large} 1 ${p1.x} ${p1.y}`;
  };
  const needle = polar(angle);
  return (
    <svg viewBox={`0 0 ${size} ${size / 2 + 34}`} className="w-full h-auto">
      {segments.map((s, i) => (
        <path key={i} d={arcPath(s.from, s.to)} stroke={s.color} strokeWidth={stroke} fill="none" strokeLinecap="butt" />
      ))}
      <line x1={cx} y1={cy} x2={needle.x} y2={needle.y} stroke="#111827" strokeWidth={4} strokeLinecap="round" />
      <circle cx={cx} cy={cy} r={6} fill="#111827" />
    </svg>
  );
}

function KpiTile({ tone, value, label, sub, onClick, testId }) {
  const toneCls = {
    red:    "border-2 border-red-500 text-red-600 bg-white dark:bg-transparent",
    pink:   "border-2 border-red-300 text-red-500 bg-white dark:bg-transparent",
    blue:   "border-2 border-blue-300 text-blue-500 bg-white dark:bg-transparent",
  }[tone] || "border-2 border-border";
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn("rounded-2xl p-4 flex flex-col items-start justify-between h-full min-h-[180px] text-left hover:scale-[1.01] transition-transform", toneCls)}
      data-testid={testId}
    >
      <div className="text-[46px] font-extrabold tabular-nums leading-none">{value}</div>
      <div className="text-[11.5px] text-muted-foreground mt-3">{sub}</div>
    </button>
  );
}

function ImpactedTrend({ trend }) {
  const data = useMemo(() => (trend || []).map((d) => ({
    date: `${new Date(d.date).getMonth() + 1}/${new Date(d.date).getDate()}`,
    critical: d.critical, high: d.high, moderate: d.moderate, low: d.low,
  })), [trend]);
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-[13.5px] font-semibold">Impacted Entity by Severity Over Time</div>
          <div className="text-[11.5px] text-muted-foreground">Last {data.length} days</div>
        </div>
        <LineIcon className="w-4 h-4 text-muted-foreground" />
      </div>
      <div className="h-[260px]">
        <ResponsiveContainer>
          <LineChart data={data} margin={{ top: 4, right: 10, bottom: 0, left: -10 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
            <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={1} />
            <YAxis tick={{ fontSize: 10 }} width={36} />
            <Tooltip contentStyle={{ fontSize: 11 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="critical" name="Critical" stroke={CRIT_META.critical.color} strokeWidth={2} dot={{ r: 2 }} />
            <Line type="monotone" dataKey="high"     name="High"     stroke={CRIT_META.high.color}     strokeWidth={2} dot={{ r: 2 }} />
            <Line type="monotone" dataKey="moderate" name="Moderate" stroke={CRIT_META.moderate.color} strokeWidth={2} dot={{ r: 2 }} />
            <Line type="monotone" dataKey="low"      name="Low"      stroke={CRIT_META.low.color}      strokeWidth={2} dot={{ r: 2 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function HealthTile({ icon: Icon, title, description, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-2xl border border-border bg-card p-4 text-left hover:border-primary/40 transition-colors flex flex-col gap-2 min-h-[180px]"
      data-testid={testId}
    >
      <div className="flex items-center gap-2">
        <Icon className="w-5 h-5 text-primary shrink-0" />
        <div className="min-w-0">
          <div className="text-[13.5px] font-semibold truncate">{title}</div>
          <div className="text-[11px] text-muted-foreground line-clamp-2">{description}</div>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center">
        {children}
      </div>
    </button>
  );
}

function HealthOk() {
  return (
    <div className="w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
      <Check className="w-8 h-8 text-emerald-500" strokeWidth={3} />
    </div>
  );
}

function InlineBar({ label, value, color, max = 15 }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-2 text-[11.5px]">
      <span className="w-20 shrink-0 text-muted-foreground text-right">{label}</span>
      <div className="flex-1 h-2.5 rounded-full bg-muted overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="w-6 tabular-nums font-semibold">{value}</span>
    </div>
  );
}

function AuditChart({ series }) {
  const data = useMemo(() => {
    const points = (series?.[0]?.points || []).length;
    return Array.from({ length: points }, (_, i) => {
      const row = { hour: i };
      (series || []).forEach((s) => { row[s.name] = s.points[i]; });
      return row;
    });
  }, [series]);
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-[13.5px] font-semibold">Unified Audit Summary</div>
          <div className="text-[11.5px] text-muted-foreground">Breakdown of policy actions · last hour</div>
        </div>
        <ClipboardList className="w-4 h-4 text-muted-foreground" />
      </div>
      <div className="h-[220px]">
        <ResponsiveContainer>
          <LineChart data={data} margin={{ top: 4, right: 10, bottom: 0, left: -10 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
            <XAxis dataKey="hour" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} width={40} />
            <Tooltip contentStyle={{ fontSize: 11 }} />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            {(series || []).map((s) => (
              <Line key={s.name} type="monotone" dataKey={s.name} stroke={s.color} strokeWidth={2} dot={false} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function LoginAttempts({ attempts }) {
  const data = [
    { name: "Success", value: attempts?.success || 0, color: "#22c55e" },
    { name: "Denied",  value: attempts?.denied  || 0, color: "#ef4444" },
  ];
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-[13.5px] font-semibold">Login Attempts</div>
          <div className="text-[11.5px] text-muted-foreground">Last {attempts?.window_days || 30} days</div>
        </div>
        <Activity className="w-4 h-4 text-muted-foreground" />
      </div>
      <div className="h-[220px]">
        <ResponsiveContainer>
          <BarChart data={data} margin={{ top: 4, right: 10, bottom: 0, left: -10 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.25} vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 10 }} width={44} />
            <Tooltip contentStyle={{ fontSize: 11 }} />
            <Bar dataKey="value" radius={[6, 6, 0, 0]} fill="#22c55e">
              {data.map((d, i) => <cell key={i} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-2 flex items-center gap-3 text-[11.5px]">
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Success · <strong>{attempts?.success || 0}</strong></span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-rose-500" /> Denied · <strong>{attempts?.denied || 0}</strong></span>
      </div>
    </div>
  );
}

function DrilldownDialog({ open, onClose, title, subtitle, children }) {
  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="tl-drilldown">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">{title}</DialogTitle>
          {subtitle && <div className="text-[12.5px] text-muted-foreground">{subtitle}</div>}
        </DialogHeader>
        {children}
      </DialogContent>
    </Dialog>
  );
}

export default function ThreatLockerApp() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [drill, setDrill] = useState(null);
  const { toast } = useToast();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "Super Admin";

  const load = useCallback(async (force = false) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/threatlocker/summary${force ? "?force=true" : ""}`);
      setData(data);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const seedSnapshot = async () => {
    setSeeding(true);
    try {
      const { data: resp } = await api.post("/threatlocker/snapshot");
      toast({
        title: "Snapshot saved",
        description: `Datapoint #${resp.total_snapshots} recorded${resp.detected_instance ? ` (instance '${resp.detected_instance}')` : ""}. Refreshing…`,
      });
      await load(true);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Snapshot failed — check the live ThreatLocker probe.";
      toast({ title: "Could not save snapshot", description: msg, variant: "destructive" });
    } finally { setSeeding(false); }
  };

  const crit = data?.criticality || {};
  const exec = data?.executive_summary || {};
  const health = data?.health || {};

  const openDrill = (config) => setDrill(config);

  return (
    <div className="space-y-5" data-testid="threatlocker-page">
      <div className="text-[12px] text-muted-foreground inline-flex items-center gap-1 flex-wrap">
        <Link to="/" className="hover:text-foreground">Dashboard</Link>
        <span aria-hidden>›</span>
        <Link to="/apps" className="hover:text-foreground">Apps</Link>
        <span aria-hidden>›</span>
        <span className="text-foreground font-medium">ThreatLocker</span>
      </div>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
            <ShieldCheck className="w-5 h-5 text-primary" />
          </div>
          <div className="min-w-0">
            <h1 className="text-[24px] font-semibold tracking-tight leading-tight">ThreatLocker</h1>
            <p className="text-[13px] text-muted-foreground mt-1">
              Defense Against Configuration posture &amp; Health Center — live from the ThreatLocker portal.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {data?.updated_at && (
            <span className="text-[11.5px] text-muted-foreground">Last updated {fmtMDT(data.updated_at)}</span>
          )}
          <Button variant="outline" size="sm" onClick={() => load(true)} className="h-9 gap-1.5" data-testid="tl-refresh-btn">
            <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} /> Refresh
          </Button>
          {isSuperAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={seedSnapshot}
              disabled={seeding || !!data?.mocked}
              className="h-9 gap-1.5"
              data-testid="tl-seed-snapshot-btn"
              title={data?.mocked ? "Live probe unavailable — snapshots require a working ThreatLocker connection." : "Persist a snapshot right now to seed the 25-day trend."}
            >
              <Camera className={cn("w-4 h-4", seeding && "animate-pulse")} />
              {seeding ? "Seeding…" : "Seed snapshot now"}
            </Button>
          )}
          <a
            href="https://portal.threatlocker.com/"
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-3 h-9 text-[12.5px] hover:border-primary/40"
            data-testid="tl-open-portal"
          >
            Open in ThreatLocker <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>

      {data?.mocked && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 text-amber-800 dark:text-amber-200 p-3 text-[12.5px] inline-flex items-start gap-2 max-w-4xl">
          <Info className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <strong>Sample data.</strong>{" "}
            {data.error_detail ? (
              <span>{data.error_detail}</span>
            ) : (
              <span>
                The live ThreatLocker probe did not succeed — showing a representative payload. Verify <code>THREATLOCKER_INSTANCE</code> and <code>THREATLOCKER_ORG_ID</code> in <code>backend/.env</code>, then hit Refresh.
              </span>
            )}
          </div>
        </div>
      )}

      {/* ── TOP STRIP ────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-3">
        {/* Environment Security Rating */}
        <button
          type="button"
          onClick={() => openDrill({ kind: "rating" })}
          className="xl:col-span-3 rounded-2xl border border-border bg-card p-4 text-left hover:border-primary/40 transition-colors"
          data-testid="tl-security-rating"
        >
          <div className="text-[13.5px] font-semibold mb-1">Environment Security Rating</div>
          <div className="text-[46px] font-extrabold tabular-nums leading-none">{data?.security_rating?.score ?? "—"}%</div>
          <GaugeArc score={data?.security_rating?.score || 0} />
          <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-wide">
            <span>WEAK</span>
            <span>STRONG</span>
          </div>
          <p className="mt-2 text-[11.5px] text-muted-foreground">
            Your organization&apos;s overall security grade is <strong>{data?.security_rating?.score ?? "—"}%</strong>,
            placing you in the <strong>{data?.security_rating?.percentile ?? "—"}<sup>th</sup></strong> percentile across all ThreatLocker protected organizations.
          </p>
        </button>

        {/* Criticality column */}
        <div className="xl:col-span-2 flex flex-col gap-2" data-testid="tl-criticality">
          {["critical", "high", "moderate", "low"].map((k) => (
            <button
              type="button"
              key={k}
              onClick={() => openDrill({ kind: "criticality", severity: k })}
              className={cn("rounded-2xl p-3 flex flex-col items-center justify-center text-center flex-1 hover:scale-[1.02] transition-transform", CRIT_META[k].ring)}
              data-testid={`tl-crit-${k}`}
            >
              <div className="text-[34px] font-extrabold leading-none">{crit[k] ?? "—"}</div>
              <div className="text-[10.5px] font-semibold uppercase tracking-wide mt-1">{CRIT_META[k].label}</div>
            </button>
          ))}
        </div>

        {/* Executive Summary */}
        <div className="xl:col-span-4 rounded-2xl border border-border bg-card p-4">
          <div className="text-[13.5px] font-semibold mb-2">Executive Summary</div>
          <div className="grid grid-cols-3 gap-3">
            <KpiTile tone="red" value={`${exec.devices_free_of_high_critical_pct ?? 0}%`} sub="of devices in your environment are free of critical and high misconfigurations." onClick={() => openDrill({ kind: "exec", metric: "devices_free" })} testId="tl-exec-devices" />
            <KpiTile tone="pink" value={`${exec.misconfig_delta_pct ?? 0}%`} sub="Since your last report, you have increased your misconfigurations by 4%." onClick={() => openDrill({ kind: "exec", metric: "delta" })} testId="tl-exec-delta" />
            <KpiTile tone="blue" value={`${exec.analyses_with_no_impact_pct ?? 0}%`} sub={`${exec.analyses_with_no_impact_count ?? 0} of ${exec.analyses_total ?? 0} analyses had no impacted entities`} onClick={() => openDrill({ kind: "exec", metric: "no_impact" })} testId="tl-exec-noimpact" />
          </div>
        </div>

        {/* Impacted trend */}
        <div className="xl:col-span-3" data-testid="tl-trend">
          <ImpactedTrend trend={data?.trend} />
        </div>
      </div>

      {/* ── HEALTH CENTER ────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 pt-2">
        <HeartPulse className="w-4 h-4 text-primary" />
        <h2 className="text-[15px] font-semibold">Health Center</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        <HealthTile icon={Check} title="Permit All Policies" description="Policies that permit all applications, removing deny by default protection." onClick={() => openDrill({ kind: "health", key: "permit_all" })} testId="tl-h-permit-all"><HealthOk /></HealthTile>

        <HealthTile icon={ShieldAlert} title="NC Insufficient Policy Coverage" description="Computer groups that either have no policies or are missing a deny all policy." onClick={() => openDrill({ kind: "health", key: "coverage" })} testId="tl-h-coverage">
          <div className="w-full space-y-2 mt-1">
            <InlineBar label="No Policies" value={health.insufficient_policy_coverage?.no_policies ?? 0} color="#f59e0b" max={15} />
            <InlineBar label="No Deny All" value={health.insufficient_policy_coverage?.no_deny_all ?? 0} color="#f59e0b" max={15} />
          </div>
        </HealthTile>

        <HealthTile icon={ShieldCheck} title="Monitor Only Policies/Computers" description={"Policies currently set to \u201CMonitor Only\u201D and computers not secured for more than 21 days."} onClick={() => openDrill({ kind: "health", key: "monitor_only" })} testId="tl-h-monitor-only">
          <div className="w-full space-y-2 mt-1">
            <InlineBar label="Policies" value={health.monitor_only?.policies ?? 0} color="#fef3c7" max={15} />
            <InlineBar label="Comp" value={health.monitor_only?.computers ?? 0} color="#fef3c7" max={15} />
          </div>
        </HealthTile>

        <HealthTile icon={FileLock2} title="Allow File Access Policies" description="Policies that are ringfenced but still allow all access to your files." onClick={() => openDrill({ kind: "health", key: "file_access" })} testId="tl-h-file-access">
          <div className="w-full mt-1">
            <InlineBar label="Policies" value={health.allow_file_access_policies?.policies ?? 0} color="#f59e0b" max={15} />
          </div>
        </HealthTile>

        <HealthTile icon={Cog} title="Single Parameter Custom Rules" description="Policies that permit an application that have a single parameter custom rule." onClick={() => openDrill({ kind: "health", key: "custom_rules" })} testId="tl-h-custom-rules"><HealthOk /></HealthTile>

        <HealthTile icon={Server} title="Remote Management" description="Policies that permit applications created by your RMM in the last 7 days." onClick={() => openDrill({ kind: "health", key: "rmm" })} testId="tl-h-rmm">
          <div className="w-full space-y-2 mt-1">
            <InlineBar label="Policies" value={health.remote_management?.policies ?? 0} color="#facc15" max={5} />
            <InlineBar label="Applications" value={health.remote_management?.applications ?? 0} color="#fef08a" max={5} />
            <InlineBar label="Rules" value={health.remote_management?.rules ?? 0} color="#fef08a" max={5} />
          </div>
        </HealthTile>

        <HealthTile icon={ClipboardList} title="Unused Policies" description="Policies not matched in the last 6 weeks." onClick={() => openDrill({ kind: "health", key: "unused" })} testId="tl-h-unused"><HealthOk /></HealthTile>

        <HealthTile icon={Cog} title="Missing Updates" description="Applications and computers with available patches that have not been applied." onClick={() => openDrill({ kind: "health", key: "updates" })} testId="tl-h-updates">
          <div className="w-full space-y-2 mt-1">
            <div className="flex items-center gap-3 text-[10.5px]">
              <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-rose-400" /> Missing</span>
              <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-muted-foreground/60" /> Total</span>
            </div>
            <InlineBar label="Applications" value={health.missing_updates?.applications ?? 0} color="#ef4444" max={health.missing_updates?.applications_total || 1} />
            <InlineBar label="Computers"    value={health.missing_updates?.computers ?? 0}    color="#ef4444" max={health.missing_updates?.computers_total || 1} />
          </div>
        </HealthTile>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <AuditChart series={data?.audit_summary?.series} />
        <LoginAttempts attempts={data?.login_attempts} />
      </div>

      <DrilldownDialog
        open={!!drill}
        onClose={() => setDrill(null)}
        title={<><ShieldCheck className="w-4 h-4 text-primary" /> ThreatLocker detail</>}
        subtitle={drill?.kind === "criticality" ? `Criticality: ${drill?.severity}` : drill?.kind === "exec" ? `Executive metric: ${drill?.metric}` : drill?.kind === "health" ? `Health check: ${drill?.key}` : "Environment Security Rating"}
      >
        <div className="text-[13px] space-y-3">
          <p className="text-muted-foreground">
            Detailed drill-down is not yet fetched from the ThreatLocker API. When we wire the
            <code className="mx-1 rounded bg-muted px-1 py-0.5 text-[11.5px]">DACAnalysisItemGetById</code>
            endpoint, this modal will list the impacted entities and remediation notes for the selected tile.
          </p>
          <div className="rounded-lg border border-border bg-muted/40 p-3">
            <div className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground mb-1">Payload for this tile</div>
            <pre className="text-[11.5px] whitespace-pre-wrap break-all">
{JSON.stringify(drill, null, 2)}
            </pre>
          </div>
          <a
            href="https://portal.threatlocker.com/"
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-primary text-[12.5px] hover:underline"
          >
            View in the ThreatLocker portal <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </DrilldownDialog>
    </div>
  );
}
